import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { pacienteService } from '../services';
import { 
  Save, 
  ArrowLeft, 
  AlertTriangle,
  User,
  FileText,
  MapPin,
  MessageSquare
} from 'lucide-react';

const CadastroPaciente = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = !!id;
  
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [formData, setFormData] = useState({
    nome: '',
    cpf: '',
    cartaoSus: '',
    rg: '',
    endereco: '',
    observacoes: ''
  });

  const carregarPaciente = useCallback(async () => {
    try {
      setLoading(true);
      const paciente = await pacienteService.getById(id);
      setFormData({
        nome: paciente.nome || '',
        cpf: paciente.cpf || '',
        cartaoSus: paciente.cartaoSus || '',
        rg: paciente.rg || '',
        endereco: paciente.endereco || '',
        observacoes: paciente.observacoes || ''
      });
    } catch (error) {
      console.error('Erro ao carregar paciente:', error);
      setMessage({ type: 'error', text: 'Erro ao carregar dados do paciente' });
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (isEditing) {
      carregarPaciente();
    }
  }, [isEditing, carregarPaciente]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Aplicar máscaras
    let maskedValue = value;
    
    if (name === 'cpf') {
      maskedValue = value
        .replace(/\D/g, '')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d{1,2})/, '$1-$2')
        .replace(/(-\d{2})\d+?$/, '$1');
    } else if (name === 'cartaoSus') {
      maskedValue = value
        .replace(/\D/g, '')
        .replace(/(\d{3})(\d)/, '$1 $2')
        .replace(/(\d{3} \d{4})(\d)/, '$1 $2')
        .replace(/(\d{3} \d{4} \d{4})(\d)/, '$1 $2')
        .replace(/(\d{3} \d{4} \d{4} \d{4})\d+?$/, '$1');
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: maskedValue
    }));
  };

  const validarFormulario = () => {
    const errors = [];
    
    if (!formData.nome.trim()) {
      errors.push('Nome é obrigatório');
    }
    
    if (formData.cpf) {
      const cpfNumeros = formData.cpf.replace(/\D/g, '');
      if (cpfNumeros.length !== 11) {
        errors.push('CPF deve ter 11 dígitos');
      }
    }
    
    if (formData.cartaoSus) {
      const cartaoNumeros = formData.cartaoSus.replace(/\D/g, '');
      if (cartaoNumeros.length !== 15) {
        errors.push('Cartão SUS deve ter 15 dígitos');
      }
    }
    
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const errors = validarFormulario();
    if (errors.length > 0) {
      setMessage({ type: 'error', text: errors.join(', ') });
      return;
    }
    
    try {
      setLoading(true);
      setMessage({ type: '', text: '' });
      
      const dadosEnvio = {
        ...formData,
        cpf: formData.cpf.replace(/\D/g, '') || null,
        cartaoSus: formData.cartaoSus.replace(/\D/g, '') || null
      };
      
      if (isEditing) {
        await pacienteService.update(id, dadosEnvio);
        setMessage({ type: 'success', text: 'Paciente atualizado com sucesso!' });
      } else {
        await pacienteService.create(dadosEnvio);
        setMessage({ type: 'success', text: 'Paciente cadastrado com sucesso!' });
      }
      
      setTimeout(() => {
        navigate('/pacientes');
      }, 2000);
      
    } catch (error) {
      console.error('Erro ao salvar paciente:', error);
      if (error.response?.data?.message) {
        setMessage({ type: 'error', text: error.response.data.message });
      } else {
        setMessage({ type: 'error', text: 'Erro ao salvar paciente' });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container">
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '30px',
        flexWrap: 'wrap',
        gap: '15px'
      }}>
        <div>
          <h1 style={{ color: '#333', margin: '0', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <User size={32} />
            {isEditing ? 'Editar Paciente' : 'Novo Paciente'}
          </h1>
          <p style={{ color: '#667', margin: '5px 0 0 0' }}>
            {isEditing ? 'Altere os dados do paciente' : 'Preencha os dados do novo paciente'}
          </p>
        </div>
        
        <button 
          type="button"
          onClick={() => navigate('/pacientes')}
          className="btn btn-secondary"
        >
          <ArrowLeft size={20} />
          Voltar
        </button>
      </div>

      {message.text && (
        <div className={`alert alert-${message.type}`} style={{ marginBottom: '20px' }}>
          {message.type === 'error' && <AlertTriangle size={20} />}
          {message.text}
        </div>
      )}

      <div className="card">
        <form onSubmit={handleSubmit}>
          <div className="form-grid">
            {/* Nome */}
            <div className="form-group" style={{ gridColumn: 'span 2' }}>
              <label className="form-label required">
                <User size={16} />
                Nome Completo
              </label>
              <input
                type="text"
                name="nome"
                value={formData.nome}
                onChange={handleChange}
                className="form-control"
                placeholder="Digite o nome completo do paciente"
                required
              />
            </div>

            {/* CPF */}
            <div className="form-group">
              <label className="form-label">
                <FileText size={16} />
                CPF
              </label>
              <input
                type="text"
                name="cpf"
                value={formData.cpf}
                onChange={handleChange}
                className="form-control"
                placeholder="000.000.000-00"
                maxLength="14"
              />
            </div>

            {/* Cartão SUS */}
            <div className="form-group">
              <label className="form-label">
                <FileText size={16} />
                Cartão SUS
              </label>
              <input
                type="text"
                name="cartaoSus"
                value={formData.cartaoSus}
                onChange={handleChange}
                className="form-control"
                placeholder="000 0000 0000 0000"
                maxLength="18"
              />
            </div>

            {/* RG */}
            <div className="form-group">
              <label className="form-label">
                <FileText size={16} />
                RG
              </label>
              <input
                type="text"
                name="rg"
                value={formData.rg}
                onChange={handleChange}
                className="form-control"
                placeholder="Digite o RG"
              />
            </div>

            {/* Endereço */}
            <div className="form-group" style={{ gridColumn: 'span 2' }}>
              <label className="form-label">
                <MapPin size={16} />
                Endereço
              </label>
              <input
                type="text"
                name="endereco"
                value={formData.endereco}
                onChange={handleChange}
                className="form-control"
                placeholder="Rua, número, bairro, cidade - CEP"
              />
            </div>

            {/* Observações */}
            <div className="form-group" style={{ gridColumn: 'span 2' }}>
              <label className="form-label">
                <MessageSquare size={16} />
                Observações
              </label>
              <textarea
                name="observacoes"
                value={formData.observacoes}
                onChange={handleChange}
                className="form-control"
                placeholder="Observações adicionais sobre o paciente"
                rows="3"
              />
            </div>
          </div>

          <div style={{ 
            display: 'flex', 
            gap: '15px', 
            justifyContent: 'flex-end',
            marginTop: '30px'
          }}>
            <button 
              type="button"
              onClick={() => navigate('/pacientes')}
              className="btn btn-secondary"
              disabled={loading}
            >
              Cancelar
            </button>
            <button 
              type="submit" 
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? (
                <div className="spinner" style={{ width: '16px', height: '16px' }}></div>
              ) : (
                <>
                  <Save size={20} />
                  {isEditing ? 'Atualizar' : 'Cadastrar'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CadastroPaciente;